import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, BookOpen, FileText, MessageCircle, Sparkles, ShieldCheck, Crown, Radio, Wand2, Plane, Briefcase, Camera } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MUSIC_HERO_COVER, MUSIC_RELEASES } from "../data/music";
import { T } from "@/locales/tr";

// Photo Paris utilisée en hero sur papicoo.fr (même propriétaire)
const HERO_BG = "https://images.unsplash.com/photo-1522582324369-2dfc36bd9275?auto=format&fit=crop&q=85&w=2000";

const tools = [
  { to: "/services", icon: Briefcase, title: "Services Papicoo", desc: "11 portes : agence com, transfert diaspora, GP, projets, dropshipping, formations, investisseurs.", testid: "tool-services" },
  { to: "/gp", icon: Plane, title: "GP Sénégal", desc: "Marketplace voyageurs ↔ expéditeurs pour la diaspora. Contact direct WhatsApp, zéro commission cachée.", testid: "tool-gp" },
  { to: "/portrait", icon: Crown, title: "Portrait présidentiable", desc: "Ta photo en costume devient un portrait de chef d'État — Tour Eiffel en fond, visage authentique.", testid: "tool-portrait" },
  { to: "/retouche", icon: Camera, title: "Retouche pro", desc: "Beauté, peau, lumière — fini studio Papicoo. Pour portraits, mode, mariage. Visage 100% authentique.", testid: "tool-retouche", image: `${process.env.PUBLIC_URL || ""}/papicoo-retouche-hero.png` },
  { to: "/live", icon: Radio, title: "Copilote Live TikTok", desc: "Assistant IA pour les lives de Cheikh Mbacké Sakho. 3 réponses ≤300 caractères avec références coraniques.", testid: "tool-live" },
  { to: "/chat", icon: MessageCircle, title: "Chat Papicoo", desc: "Assistant IA inspiré de Papicoo. Réponses appuyées sur le Coran, avec sourate et verset cités.", testid: "tool-chat" },
  { to: "/analyse", icon: BookOpen, title: "Analyse vidéo & post", desc: "Décodage profond + Constitution du pays + Coran + valeurs d'Ibrahim + 3 commentaires prêts.", testid: "tool-analyse" },
  { to: "/studio", icon: Sparkles, title: "Studio de contenu", desc: "Tes posts professionnalisés ou élevés en éveil des consciences.", testid: "tool-studio", image: `${process.env.PUBLIC_URL || ""}/papicoo-studio-hero.jpg` },
  { to: "/evoto", icon: Wand2, title: "Recettes Évoto", desc: "Presets de retouche photo calibrés au goût papicoo.fr.", testid: "tool-evoto" },
  { to: "/contrats", icon: FileText, title: "Contrats équitables", desc: "Partages de revenus justes (al-‘adl), formels, prêts à signer.", testid: "tool-contracts" },
  { to: "/reseaux", icon: ShieldCheck, title: "Mes Réseaux", desc: "Tous mes liens publics + coach IA pour pro­fes­sion­nali­ser tes plateformes.", testid: "tool-reseaux" },
];

export default function Landing() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  return (
    <div data-testid="page-landing" dir={isRtl ? "rtl" : "ltr"}>
      <Hero t={t} isRtl={isRtl} />
      <ToolsSection />
      <MusicDiscovery />
      <VotdBand />
      <NewsletterBand />
    </div>
  );
}

function Hero({ t, isRtl }) {
  return (
    <section data-testid="landing-hero" className="relative overflow-hidden min-h-[86vh] flex items-end">
      <img src={HERO_BG} alt={T("Paris au crépuscule")} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0b0b0c]/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-[#0b0b0c]/40 to-transparent" />

      <div className="relative w-full max-w-6xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-28 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-9 fade-in-up">
          <div className="papicoo-kicker mb-6">{t("landing.role")}</div>
          <h1 data-testid="hero-title" className="font-display font-semibold text-5xl sm:text-7xl xl:text-[5.5rem] text-white">
            {T("La force")}<br />{T("tranquille.")}
          </h1>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed">{t("landing.intro")}</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/chat" data-testid="hero-cta-chat" className="inline-flex items-center gap-2 px-7 py-4 text-xs uppercase tracking-[0.2em] papicoo-btn-primary">
              {T("Parler à Papicoo")} <ArrowRight size={16} className={isRtl ? "rotate-180" : ""} />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center gap-2 px-7 py-4 text-xs uppercase tracking-[0.2em] papicoo-btn-outline">
              {T("Explorer les services")}
            </Link>
          </div>

          <p data-testid="hero-ai-notice" className="mt-8 flex items-start gap-3 text-xs text-zinc-400 max-w-lg">
            <ShieldCheck size={16} className="text-[color:var(--papicoo-gold)] shrink-0 mt-0.5" />
            <span><strong className="text-zinc-200">{T("Assistant IA")}</strong> {T("inspiré de Papicoo — ce n'est pas Papicoo en personne qui répond. Aucun mot de passe demandé.")}</span>
          </p>
        </div>

        <div className="lg:col-span-3 hidden lg:block">
          <Link to="/musique" data-testid="hero-music-cover" className="group block border border-white/10 bg-[#0b0b0c]/70 backdrop-blur-md hover:border-[color:var(--papicoo-gold)] transition-colors">
            <img src={MUSIC_HERO_COVER} alt={T("Pochette — Ce qu’on ne voit pas")} className="w-full aspect-square object-contain bg-[#0a0a0b]" />
            <div className="p-5">
              <div className="papicoo-kicker">{T("Nouvelle sortie")}</div>
              <div className="font-display text-lg font-semibold uppercase text-white mt-2">{T("Ce qu’on ne voit pas")}</div>
              <div className="text-xs text-zinc-400 mt-2 inline-flex items-center gap-2 group-hover:text-[color:var(--papicoo-gold)] transition-colors">
                {T("Écouter l’extrait")} <ArrowRight size={14} className={isRtl ? "rotate-180" : ""} />
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

function ToolsSection() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
        <div className="md:col-span-7">
          <div className="papicoo-kicker">{T("Les services")}</div>
          <h2 className="font-display font-semibold uppercase text-4xl sm:text-6xl text-white mt-4 leading-[0.95]">{T("Tes outils.")}</h2>
        </div>
        <p className="md:col-span-5 text-sm text-zinc-400 italic leading-relaxed">
          {T("« Quand la prière est achevée, dispersez-vous sur la terre et recherchez la grâce d'Allah. » — Al-Jumu‘a 62:10")}
          <span className="block not-italic text-[11px] text-zinc-500 mt-2">{T("Traduction française du sens")}</span>
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {tools.map((tool) => <ToolCard key={tool.to} tool={tool} />)}
      </div>
    </section>
  );
}

function ToolCard({ tool }) {
  const Icon = tool.icon;
  return (
    <Link to={tool.to} data-testid={tool.testid} className="group bg-[#0b0b0c] hover:bg-[#141416] transition-colors duration-500 flex flex-col">
      {tool.image && (
        <div className="relative h-48 overflow-hidden">
          <img src={tool.image} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-transparent to-transparent" />
        </div>
      )}
      <div className="p-7 sm:p-8 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <Icon size={24} className="text-[color:var(--papicoo-gold)]" strokeWidth={1.5} />
          <ArrowRight size={18} className="text-zinc-500 group-hover:translate-x-1 group-hover:text-[color:var(--papicoo-gold)] transition-[transform,color] duration-300" />
        </div>
        <h3 className="font-display font-semibold uppercase tracking-wide text-xl text-white mt-8">{T(tool.title)}</h3>
        <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{T(tool.desc)}</p>
      </div>
    </Link>
  );
}

function MusicDiscovery() {
  const covers = [...new Map(MUSIC_RELEASES.map((r) => [r.cover, r])).values()];
  return (
    <section data-testid="music-discovery" className="max-w-6xl mx-auto px-5 sm:px-8 pb-24 grid md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-5">
        <div className="papicoo-kicker">{T("Musique")}</div>
        <h2 className="font-display font-semibold uppercase text-3xl sm:text-5xl text-white mt-4 leading-[0.95]">{T("Les sorties Papicoo.")}</h2>
        <p className="text-sm text-zinc-400 mt-5 max-w-md leading-relaxed">
          {T("{n} titres à découvrir en extraits de 30 secondes : « Ce qu’on ne voit pas » et « Reine d’ici et d’ailleurs » en versions Hip-Hop et Dance Mbalax.").replace("{n}", MUSIC_RELEASES.length)}
        </p>
        <Link to="/musique" data-testid="music-discovery-cta" className="mt-8 inline-flex items-center gap-2 px-7 py-4 text-xs uppercase tracking-[0.2em] papicoo-btn-primary">
          {T("Écouter les extraits")} <ArrowRight size={16} />
        </Link>
      </div>
      <div className="md:col-span-7 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
        {covers.map((r) => (
          <Link key={r.cover} to="/musique" aria-label={T("Musique — {t}").replace("{t}", r.title)} className="block bg-[#0a0a0b] group overflow-hidden">
            <img src={r.cover} alt={T("Pochette — {t}").replace("{t}", r.title)} loading="lazy" className="w-full aspect-square object-contain group-hover:scale-[1.02] transition-transform duration-700" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function NewsletterBand() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, { email, source: "landing" });
      setDone(true);
    } catch (e) { console.warn("newsletter subscribe failed:", e?.message); }
    finally { setSubmitting(false); }
  };
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-10 items-end">
      <div>
        <div className="papicoo-kicker">{T("Newsletter Papicoo")}</div>
        <h2 className="font-display font-semibold uppercase text-3xl sm:text-5xl text-white mt-4 leading-[0.95]">
          {T("Une lecture tranquille,")}<br /><span className="text-[color:var(--papicoo-gold)]">{T("tous les 3 jours.")}</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-5 max-w-md">
          {T("Spirituel · politique · sciences · philosophie · culture africaine. Analyse Papicoo au filtre coranique. Pas de spam.")}
        </p>
      </div>
      {done ? (
        <div data-testid="newsletter-success" className="text-sm text-emerald-400">{T("Inscrit. À très vite dans ta boîte.")}</div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 w-full">
          <label htmlFor="newsletter-email" className="sr-only">{T("Email")}</label>
          <input
            id="newsletter-email"
            data-testid="newsletter-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={T("ton-email@exemple.com")}
            className="flex-1 px-4 py-4 bg-[#141416] border border-white/15 text-sm text-white focus:outline-none focus:border-[color:var(--papicoo-gold)]"
          />
          <button data-testid="newsletter-submit" type="submit" disabled={submitting} className="px-6 py-4 text-xs uppercase tracking-[0.2em] papicoo-btn-primary disabled:opacity-60">
            {submitting ? "..." : T("Je m'abonne")}
          </button>
        </form>
      )}
    </section>
  );
}

// === Verset du Jour — band Landing ===
function VotdBand() {
  const [v, setV] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/votd`).then((r) => setV(r.data)).catch(() => {});
  }, []);
  return (
    <section data-testid="votd-band" className="border-y border-white/10 bg-[#0a0a0b]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
        <div className="papicoo-kicker">
          {v ? T("Verset du jour · Sourate {n} · {name} · v. {v}").replace("{n}", v.sourate_num).replace("{name}", v.sourate_name_ar_translit).replace("{v}", v.verset_num) : T("Sourate An-Najm · 53 : 39")}
        </div>
        <p className="text-2xl sm:text-4xl text-white mt-8 leading-snug font-light">
          « {v ? v.text : T("Et que l'homme n'obtient que le fruit de ses efforts.")} »
        </p>
        <div className="text-xs text-zinc-500 mt-4">{T("Traduction française du sens")}</div>
        {v && (
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/verset" data-testid="cta-verset-share" className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em] papicoo-btn-primary">
              {T("Partager ce verset")}
            </Link>
            <Link to="/coran" data-testid="cta-coran-read" className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em] papicoo-btn-outline">
              {T("Lire la sourate")}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
